import ReceiptList from "issara-sdk/apis/ReceiptList_apps_BIL";
import PatientDetailView from "issara-sdk/apis/PatientDetailView_apps_REG";
import ReceiptDetail from "issara-sdk/apis/ReceiptDetail_apps_BILM";
import InvoiceItemList from "issara-sdk/apis/InvoiceItemList_apps_BIL";
import DoctorDetail from "issara-sdk/apis/DoctorDetail_core";

// USERS
import UserPermissionView from "issara-sdk/apis/UserPermissionView_users";

// serializer
import ReceiptSerializer from "issara-sdk/types/ReceiptSerializer_apps_BIL";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import InvoiceItemImproveSerializer from "issara-sdk/types/InvoiceItemImproveSerializer_apps_BIL";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import moment from "moment";
import { adToBe } from "react-lib/utils/dateUtils";
import { HandlePrintReceipt } from "../../HISV3/BIL/sequence/BillPayment";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  GET_RECEIPT: "GET_RECEIPT",
  PRINT_RECEIPT: "PRINT_RECEIPT",
  PRINT_RECEIPT_COPPY: "PRINT_RECEIPT_COPPY",
  CANCEL_RECEIPT: "CANCEL_RECEIPT",
} as const;

export const SEQUENCE_NAME = "PaymentHistory" as const;
export const MAIN_CARD_NAME = "CardPaymentHistory" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  django?: Record<string, any>;
  PaymentHistorySequence?: PaymentHistoryType;
  receiptId?: number | null;
}>;

export type PaymentHistoryType = Partial<{
  sequenceIndex: "Action" | "START" | null;
  patientDetail: PatientSerializer;
  receiptDetail: ReceiptSerializer;
  invoiceItem: InvoiceItemImproveSerializer[];
  selecteBillingHistory: ReceiptSerializer;
  billingHistory: ReceiptSerializer[];
  billingHistoryFilter: {
    fromDate: string;
    toDate: string;
    status: string;
    subInvoiceCode: string;
  };
  permission: string;
  openModPrintCoverageDocumentError?: boolean;
}> | null;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: {};
  [ACTIONS.GET_RECEIPT]: { selectData?: ReceiptSerializer };
  [ACTIONS.PRINT_RECEIPT]: { };
  [ACTIONS.PRINT_RECEIPT_COPPY]: {
    note?: string;
    callback?: (receiptCopy: boolean, note: string) => void;
  };
  [ACTIONS.CANCEL_RECEIPT]: {
    username?: string;
    password?: string;
    note?: string;
    callback?: (modType: string, isClose: boolean) => void;
  };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  PaymentHistorySequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

let toDate = moment();

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const filterToDate = adToBe(toDate.format("DD/MM/YYYY"));

  const permission = await UserPermissionView.post({
    apiToken: controller.apiToken,
    data: {
      config_BIL_CAN_PRINT_RECEIPT_COPY: false,
    },
  });

  controller.setState(
    {
      PaymentHistorySequence: {
        sequenceIndex: "Action",
        billingHistory: [],
        billingHistoryFilter: {
          fromDate: filterToDate,
          toDate: filterToDate,
          status: "",
          subInvoiceCode: "",
        },
        permission: permission?.[0],
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH }),
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.GET_RECEIPT]: HandleGetReceipt,
    [ACTIONS.PRINT_RECEIPT]: HandlePrintReceiptMaster,
    [ACTIONS.PRINT_RECEIPT_COPPY]: HandlePrintReceiptCoppy,
    [ACTIONS.CANCEL_RECEIPT]: HandleCancelReceipt,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.PaymentHistorySequence?.billingHistoryFilter;
  const receiptId = state.receiptId;

  const data = {
    patient: state.selectedPatient?.id,
    ...(receiptId
      ? { id: receiptId }
      : {
          from_date: filter?.fromDate,
          to_date: filter?.toDate,
          status: filter?.status,
          sub_invoice_code: filter?.subInvoiceCode,
        }),
  };

  const [receiptRes, receiptErr, receiptNet] = await ReceiptList?.list({
    params: data,
    apiToken: controller.apiToken,
  });

  if (receiptRes) {
    const billingHistory = receiptId
      ? (receiptRes?.items || []).filter((item: ReceiptSerializer) => item.id === receiptId)
      : receiptRes?.items || [];
    const selectedInvoice = billingHistory[0];

    controller.setState({
      PaymentHistorySequence: {
        ...state.PaymentHistorySequence,
        billingHistory: billingHistory,
        selecteBillingHistory: {
          invoice: null,
        },
      },
      receiptId: null,
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
    });

    if (selectedInvoice) {
      HandleGetReceipt(controller, {
        sequence: "PaymentHistory",
        action: "GET_RECEIPT",
        selectData: selectedInvoice,
      });
    }
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR },
    });
  }
};

const HandleGetReceipt: Handler<Params<"GET_RECEIPT">> = async (controller, params) => {
  const state = controller.getState();

  const [
    [patientRes, patientErr, patientNet],
    [receiptRes, receiptErr, receiptNet],
    [invoiceRes, invoiceErr, invoiceNet],
  ] = await Promise.all([
    PatientDetailView.retrieve({
      pk: params.selectData?.patient_id,
      apiToken: controller.apiToken,
    }),
    ReceiptDetail.retrieve({
      pk: params.selectData?.id,
      apiToken: controller.apiToken,
    }),
    InvoiceItemList.list({
      params: {
        receipt: params.selectData?.id,
      },
      apiToken: controller.apiToken,
    }),
  ]);

  controller.setState({
    PaymentHistorySequence: {
      ...state.PaymentHistorySequence,
      patientDetail: patientRes,
      receiptDetail: receiptRes,
      invoiceItem: invoiceRes?.items,
      selecteBillingHistory: params.selectData,
    },
  });
};

const HandlePrintReceiptMaster: Handler<Params<"PRINT_RECEIPT">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PRINT_RECEIPT]: BLC_STATUS.LOADING,
    },
    errorMessage: {
      ...state.errorMessage,
      [`${ACTIONS.PRINT_RECEIPT}`]: null,
    },
  });

  const [doctorDetailRes, doctorDetailErr, doctorDetailNet] = await DoctorDetail.retrieve({
    apiToken: controller.apiToken,
    pk: state?.PaymentHistorySequence?.selecteBillingHistory?.doctor,
  });

  const prenameTh =
    (state.masterOptions?.prenameTh || []).find(
      (item: any) => item.value === doctorDetailRes?.pre_name,
    )?.text || null;

  const prenameEn =
    (state.masterOptions?.prenameEn || []).find(
      (item: any) => item.value === doctorDetailRes?.pre_name_en,
    )?.text || null;

  let doctorName =
    doctorDetailRes?.pre_name_en && doctorDetailRes?.first_name_en && doctorDetailRes?.last_name_en
      ? `${doctorDetailRes?.full_name}  (${prenameEn} ${doctorDetailRes?.first_name_en} ${doctorDetailRes?.last_name_en})`
      : doctorDetailRes?.full_name;

  const receiptData = await HandlePrintReceipt(controller as any, {
    id: state?.PaymentHistorySequence?.selecteBillingHistory?.id,
    doctorName: doctorName,
    invoice: state?.PaymentHistorySequence?.selecteBillingHistory?.invoice,
    isMeBilling: true,
    isShowBackground: true,
  });

  if (receiptData?.receipt_coverage_document_list?.length === 0) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_RECEIPT_COPPY]: BLC_STATUS.SUCCESS,
      },
      PaymentHistorySequence: {
        ...state.PaymentHistorySequence,
        openModPrintCoverageDocumentError: true,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_RECEIPT_COPPY]: BLC_STATUS.SUCCESS,
      },
    });
  }
};

const HandlePrintReceiptCoppy: Handler<Params<"PRINT_RECEIPT_COPPY">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PRINT_RECEIPT_COPPY]: BLC_STATUS.LOADING,
    },
    errorMessage: {
      ...state.errorMessage,
      [`${ACTIONS.PRINT_RECEIPT_COPPY}`]: null,
    },
  });

  if (params.note === "") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_RECEIPT_COPPY]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [`${ACTIONS.PRINT_RECEIPT_COPPY}`]: "กรุณาระบุเหตุผลการพิมพ์สำเนาใบเสร็จ",
      },
    });
  } else {
    const [doctorDetailRes, doctorDetailErr, doctorDetailNet] = await DoctorDetail.retrieve({
      apiToken: controller.apiToken,
      pk: state?.PaymentHistorySequence?.selecteBillingHistory?.doctor,
    });

    const prenameTh =
      (state.masterOptions?.prenameTh || []).find(
        (item: any) => item.value === doctorDetailRes?.pre_name,
      )?.text || null;

    const prenameEn =
      (state.masterOptions?.prenameEn || []).find(
        (item: any) => item.value === doctorDetailRes?.pre_name_en,
      )?.text || null;

    let doctorName =
      doctorDetailRes?.pre_name_en &&
      doctorDetailRes?.first_name_en &&
      doctorDetailRes?.last_name_en
        ? `${doctorDetailRes?.full_name}  (${prenameEn} ${doctorDetailRes?.first_name_en} ${doctorDetailRes?.last_name_en})`
        : doctorDetailRes?.full_name;

    const receiptData = await HandlePrintReceipt(controller as any, {
      id: state?.PaymentHistorySequence?.selecteBillingHistory?.id,
      note: params.note,
      isWatermark: true,
      doctorName: doctorName,
      invoice: state?.PaymentHistorySequence?.selecteBillingHistory?.invoice,
      isMeBilling: true,
    });

    if (receiptData?.receipt_coverage_document_list?.length === 0) {
      controller.setState({
        buttonLoadCheck: {
          ...state.buttonLoadCheck,
          [BTN_ACTS.PRINT_RECEIPT_COPPY]: BLC_STATUS.SUCCESS,
        },
        PaymentHistorySequence: {
          ...state.PaymentHistorySequence,
          openModPrintCoverageDocumentError: true,
        },
      });
    } else {
      params.callback?.(false, "");
      controller.setState({
        buttonLoadCheck: {
          ...state.buttonLoadCheck,
          [BTN_ACTS.PRINT_RECEIPT_COPPY]: BLC_STATUS.SUCCESS,
        },
      });
    }
  }
};

const HandleCancelReceipt: Handler<Params<"CANCEL_RECEIPT">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CANCEL_RECEIPT]: BLC_STATUS.LOADING,
    },
    errorMessage: {
      ...state.errorMessage,
      [`${ACTIONS.CANCEL_RECEIPT}`]: null,
    },
  });

  const cancelReceipt = await ReceiptDetail.delete({
    pk: state.PaymentHistorySequence?.selecteBillingHistory?.id,
    data: {
      username: params?.username,
      password: params?.password,
      note: params?.note,
    },
    apiToken: controller.apiToken,
    extra: {
      device: controller.data.device,
      division: controller.data.division,
    },
  });

  if (cancelReceipt?.[1]) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CANCEL_RECEIPT]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [`${ACTIONS.CANCEL_RECEIPT}`]: cancelReceipt?.[1],
      },
    });

    params.callback?.("error", true);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CANCEL_RECEIPT]: BLC_STATUS.SUCCESS,
      },
      errorMessage: {
        ...state.errorMessage,
        [`${ACTIONS.CANCEL_RECEIPT}`]: null,
      },
    });

    params.callback?.("success", false);

    HandleSearch(controller, {
      sequence: "PaymentHistory",
      action: "SEARCH",
    });
  }
};
